import { featureCollection, polygon as turfPolygon, union } from "@turf/turf";
import type * as THREE from "three";
import type { Palette } from "@designboticteam/core";
import { Polygon } from "@designboticteam/workspace-3d";
import type { ParcelSelectionMode, SelectedParcel } from "../../services/Kolega3DStoreService";
import type { PickedParcel } from "../../services/ParcelPickerService";

type ParcelRecord = PickedParcel & { preview: Polygon };

type ParcelSelectionParams = {
	scene: THREE.Object3D;
	palette: Palette;
	/** Maps a WGS84 ring onto scene points — needed for the outline of a union of parcels. */
	ringToScenePoints: (ring: Array<[number, number]>) => THREE.Vector3[];
};

/**
 * ZoneTool's picking-mode draft: the parcels picked so far, their previews in the scene and the
 * rules that keep the selection turnable into zones. Like a half-drawn polygon, it is thrown away
 * when the tool leaves the mode.
 */
class ParcelSelection {
	private readonly records = new Map<string, ParcelRecord>();
	private mode: ParcelSelectionMode = "single";

	constructor(private readonly params: ParcelSelectionParams) {}

	public getMode(): ParcelSelectionMode {
		return this.mode;
	}

	public setMode(mode: ParcelSelectionMode): void {
		this.mode = mode;

		if (mode === "single" && this.records.size > 1) {
			const lastId = [...this.records.keys()].at(-1);

			for (const id of [...this.records.keys()]) {
				if (id !== lastId) {
					this.removeRecord(id);
				}
			}
		}
	}

	public getItems(): ReadonlyArray<SelectedParcel> {
		return [...this.records.values()].map(({ id, numer, areaM2 }) => ({ id, numer, areaM2 }));
	}

	public isEmpty(): boolean {
		return this.records.size === 0;
	}

	/** Adds the parcel, or removes it when it is already selected. Returns false when the rules rejected it. */
	public toggle(parcel: PickedParcel): boolean {
		if (this.records.has(parcel.id)) {
			return this.remove(parcel.id);
		}

		// In multiple mode every added parcel must keep the selection a single connected polygon —
		// it has to share an edge with the current selection so the union always yields one zone.
		if (this.mode === "multiple" && this.records.size > 0) {
			const candidateRings = [...this.records.values()].map((record) => record.ringWgs84).concat([parcel.ringWgs84]);

			if (this.isSingleConnectedPolygon(candidateRings) === false) {
				return false;
			}
		}

		if (this.mode === "single") {
			this.clear();
		}

		this.addRecord(parcel);
		return true;
	}

	/** Returns false when the removal would split the remaining selection into islands. */
	public remove(id: string): boolean {
		if (this.records.has(id) === false) {
			return false;
		}

		if (this.mode === "multiple") {
			const remainingRings = [...this.records.values()].filter((record) => record.id !== id).map((record) => record.ringWgs84);

			if (remainingRings.length >= 2 && this.isSingleConnectedPolygon(remainingRings) === false) {
				return false;
			}
		}

		this.removeRecord(id);
		return true;
	}

	public clear(): void {
		for (const id of [...this.records.keys()]) {
			this.removeRecord(id);
		}
	}

	/** One open outer ring (scene points) per connected part of the selection. */
	public getMergedOutlines(): Array<THREE.Vector3[]> {
		const records = [...this.records.values()];

		if (records.length === 0) {
			return [];
		}

		// A lone parcel keeps its own points — no union, no wgs84 round trip.
		if (records.length === 1) {
			return [records[0].scenePoints];
		}

		const merged = union(featureCollection(records.map((record) => turfPolygon([this.closeRing(record.ringWgs84)]))));

		if (merged === null) {
			return records.map((record) => record.scenePoints);
		}

		const parts = merged.geometry.type === "Polygon" ? [merged.geometry.coordinates] : merged.geometry.coordinates;

		return parts.map((part) => this.params.ringToScenePoints((part[0] as Array<[number, number]>).slice(0, -1)));
	}

	private addRecord(parcel: PickedParcel): void {
		const { palette } = this.params;
		const preview = new Polygon({
			label: parcel.numer,
			lineColor: palette.main.primary,
			fillColor: palette.main.primary,
			fillOpacity: 0.4,
		});
		preview.setPoints({ points: parcel.scenePoints });
		preview.finish();

		this.params.scene.add(preview.sceneObject);
		this.records.set(parcel.id, { ...parcel, preview });
	}

	private removeRecord(id: string): void {
		const record = this.records.get(id);

		if (record === undefined) {
			return;
		}

		record.preview.sceneObject.removeFromParent();
		record.preview.dispose();
		this.records.delete(id);
	}

	private isSingleConnectedPolygon(rings: Array<Array<[number, number]>>): boolean {
		if (rings.length <= 1) {
			return true;
		}

		const merged = union(featureCollection(rings.map((ring) => turfPolygon([this.closeRing(ring)]))));

		return merged !== null && merged.geometry.type === "Polygon";
	}

	private closeRing(ring: Array<[number, number]>): Array<[number, number]> {
		if (ring.length === 0) {
			return ring;
		}

		const first = ring[0];
		const last = ring.at(-1);

		if (last !== undefined && first[0] === last[0] && first[1] === last[1]) {
			return ring;
		}

		return [...ring, first];
	}
}

export { ParcelSelection, type ParcelSelectionParams };
